import type { Option } from "@tento-lona/sheets";
import type { SheetScrollPort } from "@tento-ui/components/sheet/sheet-layout";

/**
 * The sheet's vertical scroll position, read once per frame: the position the
 * scroll event already reported when there is one, else a DOM read (which
 * forces layout when the frame has written before it). In every arm, not only
 * under `scroll-frame-deadline`: with the flag off the DOM read on every
 * frame cost a forced layout of 2.3-2.6 ms, while the observed position
 * missed 0 times in every measured deadline run. A miss still
 * falls back to the DOM and is reported through `onDomRead`.
 *
 * A focus, blur or resize can move the viewport before any scroll event
 * reports it (a focused cell scrolled into view); `expire()` makes the next
 * read a fresh DOM read.
 */
export class SheetFrameScrollTop {
  #last: Option<{ readonly frame: number; readonly top: Option<number> }> = null;
  #fresh = false;

  constructor(readonly deps: SheetFrameScrollTop.Deps) {}

  /** This frame's scroll top (null before the sheet has a scroll port position). */
  read(): Option<number> {
    const frame = this.deps.frameNumber();
    const last = this.#last;
    if (last != null && last.frame === frame) return last.top;
    const port = this.deps.port();
    const observed = this.#fresh ? null : port.observedScrollPosition();
    this.#fresh = false;
    if (observed == null) this.deps.onDomRead(frame);
    const top = (observed ?? port.getScrollPosition())?.top ?? null;
    this.#last = { frame, top };
    return top;
  }

  /** A scroll event reported a new position: the next read takes it, even within this frame. */
  moved(): void {
    this.#last = null;
  }

    /** The next read comes from the DOM: the viewport may have moved without a scroll event yet. */
  expire(): void {
    this.#last = null;
    this.#fresh = true;
  }
}

/** Types that go with `SheetFrameScrollTop`. */
export namespace SheetFrameScrollTop {
  /** The scroll port's two reads (`SheetLayoutPort` in production). */
  export type Port = Pick<SheetScrollPort, "getScrollPosition" | "observedScrollPosition">;
  export type Deps = {
    readonly port: () => Port;
    /** The fastdom frame number (`$$.frameNumber()`). */
    readonly frameNumber: () => number;
    /** A frame whose position came from the DOM (the `sheet/scroll-top/dom-read` trace). */
    readonly onDomRead: (frame: number) => void;
  };
}
